// Genre categorization for admin/assistant track modules.
// Groups raw genre strings into friendly categories so staff can see
// how many tracks exist per style and spot gaps to fill.

export const GENRE_CATEGORIES = [
  {
    id: 'electronica',
    label: 'ELECTRÓNICA',
    color: '#60a5fa', // blue
    match: [
      'electronic', 'electrónica', 'electronica',
      'house', 'deep house', 'tech house', 'afro house', 'progressive house', 'future house', 'minimal house', 'melodic house',
      'techno', 'trance', 'edm', 'electro', 'dubstep', 'drum & bass', 'dnb', 'dance', 'club',
      'garage', 'breakbeat', 'big room',
    ],
  },
  {
    id: 'reggaeton',
    label: 'REGGAETON',
    color: '#f472b6', // pink
    match: ['reggaeton', 'reguetón', 'regueton', 'perreo'],
  },
  {
    id: 'dembow',
    label: 'DEMBOW',
    color: '#ec4899', // fuchsia
    match: ['dembow'],
  },
  {
    id: 'trap',
    label: 'TRAP',
    color: '#fb923c', // orange
    match: ['trap', 'trap latino', 'latin trap'],
  },
  {
    id: 'hiphop',
    label: 'HIP-HOP / RAP',
    color: '#fbbf24', // amber
    match: ['hip-hop', 'hip hop', 'hiphop', 'rap', 'drill', 'boom bap', 'old school'],
  },
  {
    id: 'salsa',
    label: 'SALSA',
    color: '#22c55e', // green
    match: ['salsa', 'timba', 'salsa brava', 'salsa romántica', 'salsa romantica'],
  },
  {
    id: 'bachata',
    label: 'BACHATA',
    color: '#ef4444', // red
    match: ['bachata', 'bachata urbana', 'bachata sensual'],
  },
  {
    id: 'merengue',
    label: 'MERENGUE',
    color: '#10b981', // emerald
    match: ['merengue', 'merengues', 'mambo'],
  },
  {
    id: 'mexicana',
    label: 'MÚSICA MEXICANA',
    color: '#84cc16', // lime
    match: [
      'mexicana', 'música mexicana', 'musica mexicana',
      'regional mexicano', 'regional', 'corrido', 'corridos', 'corridos tumbados', 'tumbado',
      'banda', 'norteño', 'norteno', 'mariachi', 'ranchera', 'sierreño', 'sierreno', 'cumbia mexicana',
    ],
  },
  {
    id: 'baladas',
    label: 'BALADAS',
    color: '#c084fc', // purple
    match: ['balada', 'baladas', 'bolero', 'romántica', 'romantica', 'romance'],
  },
  {
    id: 'cristiana',
    label: 'MÚSICA CRISTIANA',
    color: '#38bdf8', // sky
    match: ['cristiana', 'cristiano', 'música cristiana', 'musica cristiana', 'gospel', 'worship', 'adoración', 'adoracion', 'alabanza'],
  },
  {
    id: 'pop',
    label: 'POP / POP LATINO',
    color: '#fb7185', // rose
    match: ['pop', 'pop latino', 'latin pop', 'synth pop', 'synthpop', 'indie pop'],
  },
  {
    id: 'rnb',
    label: 'R&B / SOUL',
    color: '#a78bfa', // violet
    match: ['r&b', 'rnb', 'soul', 'neo-soul', 'neosoul'],
  },
  {
    id: 'afro',
    label: 'AFRO',
    color: '#f59e0b', // orange-dark
    match: ['afrobeats', 'afrobeat', 'amapiano', 'afro pop', 'dancehall'],
  },
  {
    id: 'tropical',
    label: 'TROPICAL (OTROS)',
    color: '#34d399', // emerald-light
    match: ['cumbia', 'vallenato', 'guaracha', 'tropical', 'champeta'],
  },
  {
    id: 'chill',
    label: 'CHILL / LO-FI',
    color: '#22d3ee', // cyan
    match: ['lo-fi', 'lofi', 'chill', 'chillout', 'ambient', 'downtempo'],
  },
  {
    id: 'instrumentales',
    label: 'BEATS / INSTRUMENTALES',
    color: '#E8720C', // brand orange
    match: [
      'instrumental', 'instrumentales', 'beats', 'beat',
      'beats sin letra', 'beat sin letra', 'sin letra', 'type beat',
      'karaoke',
    ],
  },
  {
    id: 'rock',
    label: 'ROCK / ALT',
    color: '#dc2626', // red-dark
    match: ['rock', 'alternative', 'indie', 'punk', 'metal', 'alt rock'],
  },
  {
    id: 'otros',
    label: 'SIN CATEGORÍA',
    color: '#9ca3af', // gray
    match: [], // catches any genre not matching above
  },
];

const LOW_THRESHOLD = 5; // below this → highlight as gap

function norm(s) {
  return (s || '').toString().trim().toLowerCase();
}

export function categoryForGenre(genre) {
  const g = norm(genre);
  if (!g) return 'otros';
  // Exact-match pass first (avoids "pop" eating "pop latino", etc.)
  for (const cat of GENRE_CATEGORIES) {
    if (cat.id === 'otros') continue;
    if (cat.match.some(m => g === m)) return cat.id;
  }
  // Then fuzzy includes
  for (const cat of GENRE_CATEGORIES) {
    if (cat.id === 'otros') continue;
    if (cat.match.some(m => g.includes(m))) return cat.id;
  }
  return 'otros';
}

// Build stats: { byCategory: { catId: { count, genres: { rawGenre: count } } }, totalCount }
export function buildGenreStats(tracks) {
  const result = {};
  GENRE_CATEGORIES.forEach(c => { result[c.id] = { count: 0, genres: {} }; });
  let total = 0;
  (tracks || []).forEach(t => {
    total += 1;
    const raw = (t.genre || '').trim() || 'Sin género';
    const catId = categoryForGenre(raw);
    result[catId].count += 1;
    result[catId].genres[raw] = (result[catId].genres[raw] || 0) + 1;
  });
  return { byCategory: result, total };
}

export function isLowCount(count) { return count > 0 && count < LOW_THRESHOLD; }
export function isEmpty(count) { return count === 0; }
